import React, { useEffect, useState } from 'react'
import './custom-node.scss'
import { Handle, NodeProps, NodeToolbar, Position, useReactFlow } from '@xyflow/react'
import { ApiVersion } from '../../../../enums/api-version.enum'
import { useService } from '@pushly/aqe/lib/hooks'
import { NotificationService } from '../../../../services'
import { getEdgeId } from '../../helpers'
import { JourneyNodeIcon } from './journey-node-icon'
import { JourneyNodeLabel } from './journey-node-label'
import { getClassNames } from '../../../../_utils/classnames'
import { useJourneyContext } from '../../context'
import { parseLoadedVariant } from '../../../../components/notification-builder/helpers'
import { AppState } from '../../../../stores/app'
import { parseDomainNotificationBuildFromVariant } from '../../action-helpers'
import { getItemStrategy, isSendWithParamsActionStep, JourneyNode, JourneyStep } from '../../types/journey-nodes'
import { InvalidJourneyComponent } from '../../edge-types/custom-edge/invalid-journey-component'
import { JourneyNodeInteractionActions } from './journey-node-interaction-actions'
import './custom-node.scss'
import { StepType } from '@pushly/models/lib/enums/journeys'

export const CustomNode = ({ id, data, targetPosition, sourcePosition }: NodeProps<JourneyNode>) => {
    const [state, _dispatch] = useJourneyContext()
    const { getNode, setNodes } = useReactFlow<JourneyNode>()
    const appState = useService(AppState)
    const notifSvc = useService(NotificationService)
    const [loadingDetails, setLoadingDetails] = useState(false)

    const step = data.step

    // onComponentMount effect - fetch step details dependencies
    useEffect(() => {
        const getAction = async () => {
            setLoadingDetails(true)

            try {
                if (step.type !== StepType.ACTION) return

                const isNotificationAction = isSendWithParamsActionStep(step)
                if (!isNotificationAction) return

                const actionConfig = step.configuration
                const notificationId = actionConfig.params.notification_id
                if (!notificationId) return

                const notif = await notifSvc.fetchNotificationById(state.domain.id, notificationId, {
                    query: { include_schedules: false },
                    version: ApiVersion.V4,
                })
                if (!notif) return

                const itemStrategy = getItemStrategy(step)

                // Only patch common notification fields; preserve existing params
                // Update the tracked data and push into the flow store
                const updatedStep = {
                    ...step,
                    configuration: {
                        ...actionConfig,
                        params: {
                            ...actionConfig.params,
                            title: notif?.template?.channels?.default?.title,
                            notification: parseDomainNotificationBuildFromVariant(
                                parseLoadedVariant(notif),
                                appState.flags,
                                state.domain,
                            ),
                            notification_id: notificationId,
                            ...(itemStrategy ? { item_strategy: itemStrategy } : {}),
                        } as typeof actionConfig.params,
                    },
                }

                data.resetInitialState({
                    step: updatedStep as JourneyStep,
                    uiMeta: { ...data.uiMeta },
                })

                setNodes((nodes) => nodes.filter((n) => n.id !== id).concat([{ ...getNode(id)!, data }]))
            } finally {
                setLoadingDetails(false)
            }
        }

        getAction()
    }, [])

    return (
        <React.Fragment>
            {![StepType.TRIGGER].includes(step.type) && (
                <Handle type="target" position={targetPosition ?? Position.Top} id={getEdgeId(id)} />
            )}
            <NodeToolbar isVisible={!!data.uiMeta.invalid} position={Position.Left} className="journey-invalid-label">
                <InvalidJourneyComponent content={data.uiMeta.invalid?.reason} />
            </NodeToolbar>
            <div
                className={getClassNames(
                    null,
                    `custom-node-wrapper type-${step.type} ${
                        step.configuration.type ? `subType-${String(step.configuration.type).toLowerCase()}` : ''
                    }`,
                    {
                        invalid: data.uiMeta.invalid,
                    },
                )}
            >
                <div className="custom-node-border" />
                <div className={getClassNames(null, 'custom-node', 'no-drag')}>
                    <JourneyNodeIcon id={id} node={step} />
                    <JourneyNodeLabel node={step} loading={loadingDetails} />
                    {![StepType.TRIGGER, StepType.EXIT].includes(step.type) && (
                        <JourneyNodeInteractionActions nodeId={id} />
                    )}
                </div>
            </div>
            {step.type !== StepType.EXIT && (
                <Handle type="source" position={sourcePosition ?? Position.Bottom} id={getEdgeId(id)} />
            )}
        </React.Fragment>
    )
}
